import { beautify, returnArray } from "../../../utils/helpers";

export type TTrialBalanceAccount = {
  id?: string;
  account_code?: string;
  account_name: string;
  debit: number;
  credit: number;
  children: TTrialBalanceAccount[];
};

export type TTrialBalanceGroup = {
  key: string;
  label: string;
  accounts: TTrialBalanceAccount[];
  debit: number;
  credit: number;
};

export type TTrialBalance = {
  groups: TTrialBalanceGroup[];
  totalDebits: number;
  totalCredits: number;
  difference: number;
  isBalanced: boolean;
};

// the order the sections are read in on a trial balance
const TYPE_ORDER = [
  "asset",
  "assets",
  "liability",
  "liabilities",
  "equity",
  "income",
  "revenue",
  "cost_of_goods_sold",
  "expense",
  "expenses",
  "operating_expenses",
];

const TYPE_LABELS: Record<string, string> = {
  asset: "Assets",
  assets: "Assets",
  liability: "Liabilities",
  liabilities: "Liabilities",
  equity: "Equity",
  income: "Income",
  revenue: "Income",
  expense: "Expenses",
  expenses: "Expenses",
};

// the report is read off whichever of these the endpoint sends
const LIST_KEYS = ["results", "result", "accounts", "groups", "data", "items"];
const DEBIT_KEYS = [
  "debit",
  "debit_amount",
  "total_debit",
  "debit_total",
  "debits",
  "debit_amount_by_date",
];
const CREDIT_KEYS = [
  "credit",
  "credit_amount",
  "total_credit",
  "credit_total",
  "credits",
  "credit_amount_by_date",
];

const num = (value: any) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

const round2 = (value: number) => Math.round(value * 100) / 100;

const readNumber = (item: any, keys: string[]) => {
  const key = keys.find(
    (candidate) =>
      item?.[candidate] !== undefined && item?.[candidate] !== null,
  );
  return key === undefined ? undefined : num(item[key]);
};

/** an account carries its two columns as the endpoint names them; one that only
 * carries a signed balance falls on the side that balance reads on */
const readSides = (item: any) => {
  const debit = readNumber(item, DEBIT_KEYS);
  const credit = readNumber(item, CREDIT_KEYS);

  if (debit !== undefined || credit !== undefined) {
    return { debit: debit ?? 0, credit: credit ?? 0 };
  }

  const amount = num(item?.amount);
  return amount >= 0
    ? { debit: amount, credit: 0 }
    : { debit: 0, credit: -amount };
};

/** names come through readable, so only the coded ones need beautifying */
const readName = (item: any) => {
  const name = String(
    item?.account_name || item?.name || item?.label || "",
  ).trim();
  return name.includes("_") ? beautify(name) : name;
};

const typeLabel = (type?: string) =>
  type ? TYPE_LABELS[String(type).toLowerCase()] ?? beautify(type) : "Accounts";

const typeRank = (type?: string) => {
  const rank = TYPE_ORDER.indexOf(String(type).toLowerCase());
  return rank === -1 ? TYPE_ORDER.length : rank;
};

const buildAccount = (item: any): TTrialBalanceAccount => {
  const children = returnArray(item?.children).map(buildAccount);
  const own = readSides(item);

  const rolled = children.reduce(
    (sum, child) => ({
      debit: sum.debit + child.debit,
      credit: sum.credit + child.credit,
    }),
    { debit: 0, credit: 0 },
  );

  const carriesOwnTotals = !children.length || own.debit || own.credit;

  return {
    id: item?.id,
    account_code: item?.general_ledger_code || item?.account_code || "",
    account_name: readName(item),
    debit: round2(carriesOwnTotals ? own.debit : rolled.debit),
    credit: round2(carriesOwnTotals ? own.credit : rolled.credit),
    children,
  };
};

const toAccounts = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const listKey = LIST_KEYS.find((key) => Array.isArray(value[key]));
  if (listKey) return returnArray(value[listKey]);

  return [value];
};

const readGroups = (data: any): { key: string; accounts: any[] }[] => {
  if (!data) return [];

  const flat = Array.isArray(data)
    ? data
    : LIST_KEYS.map((key) => data[key]).find(Array.isArray);

  if (flat) {
    const keys = [
      ...new Set(
        flat.map((item: any) =>
          String(item?.account_type || item?.type || item?.section || ""),
        ),
      ),
    ] as string[];

    return keys.map((key) => ({
      key,
      accounts: flat.filter(
        (item: any) =>
          String(item?.account_type || item?.type || item?.section || "") ===
          key,
      ),
    }));
  }

  if (typeof data !== "object") return [];

  return Object.entries(data)
    .filter(([, value]) => value && typeof value === "object")
    .map(([key, value]: [string, any]) => ({
      key: String(value?.account_type || key),
      accounts: toAccounts(value),
    }));
};

export const buildTrialBalance = (data: any): TTrialBalance => {
  const groups: TTrialBalanceGroup[] = readGroups(data)
    .map(({ key, accounts }) => {
      const built = accounts.map(buildAccount);

      // the section foots its own top level; children are already rolled into it
      const totals = built.reduce(
        (sum, account) => ({
          debit: sum.debit + account.debit,
          credit: sum.credit + account.credit,
        }),
        { debit: 0, credit: 0 },
      );

      return {
        key,
        label: typeLabel(key),
        accounts: built,
        debit: round2(totals.debit),
        credit: round2(totals.credit),
      };
    })
    .filter((group) => group.accounts.length)
    .sort((a, b) => typeRank(a.key) - typeRank(b.key));

  const totalDebits = round2(
    groups.reduce((sum, group) => sum + group.debit, 0),
  );
  const totalCredits = round2(
    groups.reduce((sum, group) => sum + group.credit, 0),
  );
  const difference = round2(totalDebits - totalCredits);

  return {
    groups,
    totalDebits,
    totalCredits,
    difference,
    isBalanced: difference === 0,
  };
};
